import React, { useMemo } from "react";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { formatBirthMonthYearDisplay } from "../shared/utils/birthMonthYear";
import { displayEmailOrDash, isPlaceholderEmail } from "../shared/utils/displayEmail";
import { getGenderDisplayLabel } from "../shared/utils/genderOptions";
import { getGradeLabel } from "../shared/utils/gradeOptions";
import { getRaceEthnicityDisplayLabel } from "../shared/utils/childProfileOptions";
import {
  getEducationLevelLabel,
  getHearAboutUsDisplayLabel,
} from "../shared/utils/parentProfileOptions";
import { getPhotographyConsentLabel } from "../shared/utils/photographyConsent";
import { ACCOUNT_SIGNUP_FORM_CLASS } from "../shared/utils/accountSignupForm";

function formatCreatedDate(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function hasText(v) {
  if (v == null) return false;
  const s = String(v).trim();
  return s !== "" && s !== "—";
}

function hasRaceEthnicity(selectionsRaw, otherRaw) {
  return getRaceEthnicityDisplayLabel(selectionsRaw, otherRaw) !== "—";
}

function ReadField({ label, value, fullWidth = false }) {
  const text = value != null && String(value).trim() !== "" ? String(value) : "—";
  return (
    <div className={`form-field${fullWidth ? " form-field--full" : ""}`}>
      <label>{label}</label>
      <div className="ProfileInfo-readonly-value" title={text !== "—" ? text : undefined}>
        {text}
      </div>
    </div>
  );
}

/** Chunk fields into 2-column form-rows (fullWidth items get their own row). */
function renderFormRows(fields) {
  const rows = [];
  let i = 0;
  let rowKey = 0;
  while (i < fields.length) {
    const a = fields[i];
    if (a.fullWidth) {
      rows.push(
        <div className="form-row" key={`r-${rowKey++}`}>
          <ReadField label={a.label} value={a.value} fullWidth />
        </div>
      );
      i += 1;
      continue;
    }
    const b = fields[i + 1] && !fields[i + 1].fullWidth ? fields[i + 1] : null;
    rows.push(
      <div className="form-row" key={`r-${rowKey++}`}>
        <ReadField label={a.label} value={a.value} />
        {b ? <ReadField label={b.label} value={b.value} /> : <div className="form-field" />}
      </div>
    );
    i += b ? 2 : 1;
  }
  return rows;
}

/**
 * Shared profile info content — field rhythm/typography matches account registration
 * (`modal-form EventCalendar-modal-form`).
 */
function ProfileInfoContent({
  firstName = "—",
  lastName = "—",
  email = "—",
  phone = "—",
  titleAction,
  titleId,
  birthday,
  gender,
  preferredName,
  schoolDistrict,
  raceEthnicity,
  raceEthnicityOther,
  photographyConsent,
  parentName,
  parentFirstName,
  parentLastName,
  parentEmail,
  parentPhone,
  parentPreferredName,
  parentGender,
  parentZipCode,
  parentHighestEducationLevel,
  parentHearAboutUs,
  parentHearAboutUsOther,
  grade,
  school,
  createdDate,
  showAllRegistrationFields = false,
  section = "all",
  /** When false, omit page-level Account / Profile info chrome (ModalShell supplies the title). */
  showPageChrome = true,
}) {
  const profileFields = useMemo(() => {
    const list = [];
    const first = hasText(firstName) ? String(firstName).trim() : "—";
    const last = hasText(lastName) ? String(lastName).trim() : "—";
    list.push({ label: "First Name", value: first });
    list.push({ label: "Last Name", value: last });
    if (showAllRegistrationFields || hasText(preferredName)) {
      list.push({
        label: "Preferred Name",
        value: hasText(preferredName) ? String(preferredName).trim() : "—",
      });
    }
    if (showAllRegistrationFields || (birthday != null && birthday !== "")) {
      list.push({
        label: "Date of birth",
        value: formatBirthMonthYearDisplay(birthday),
      });
    }
    if (showAllRegistrationFields || hasText(gender)) {
      list.push({ label: "Gender", value: getGenderDisplayLabel(gender) });
    }
    if (showAllRegistrationFields || hasText(grade)) {
      list.push({ label: "Grade", value: getGradeLabel(grade) });
    }
    if (showAllRegistrationFields || hasText(school)) {
      list.push({
        label: "School",
        value: hasText(school) ? String(school).trim() : "—",
      });
    }
    if (showAllRegistrationFields || hasText(schoolDistrict)) {
      list.push({
        label: "School District",
        value: hasText(schoolDistrict) ? String(schoolDistrict).trim() : "—",
      });
    }
    if (showAllRegistrationFields || hasRaceEthnicity(raceEthnicity, raceEthnicityOther)) {
      list.push({
        label: "Race / Ethnicity",
        value: getRaceEthnicityDisplayLabel(raceEthnicity, raceEthnicityOther),
        fullWidth: true,
      });
    }
    list.push({ label: "Email", value: displayEmailOrDash(email) });
    list.push({ label: "Phone", value: formatPhoneDisplay(phone) });
    if (
      showAllRegistrationFields ||
      photographyConsent === true ||
      photographyConsent === false
    ) {
      list.push({
        label: "Photo consent",
        value: getPhotographyConsentLabel(photographyConsent),
      });
    }
    list.push({
      label: "Created",
      value: formatCreatedDate(createdDate),
    });
    return list;
  }, [
    firstName,
    lastName,
    email,
    phone,
    birthday,
    gender,
    preferredName,
    schoolDistrict,
    raceEthnicity,
    raceEthnicityOther,
    photographyConsent,
    grade,
    school,
    createdDate,
    showAllRegistrationFields,
  ]);

  const parentFields = useMemo(() => {
    const list = [];
    const resolvedFirst = hasText(parentFirstName)
      ? String(parentFirstName).trim()
      : "";
    const resolvedLast = hasText(parentLastName)
      ? String(parentLastName).trim()
      : "";
    // Fallback when callers only pass combined parentName (older payloads).
    let first = resolvedFirst;
    let last = resolvedLast;
    if (!first && !last && hasText(parentName)) {
      const parts = String(parentName).trim().split(/\s+/);
      first = parts[0] || "";
      last = parts.slice(1).join(" ");
    }
    const showParentExtras =
      showAllRegistrationFields ||
      hasText(first) ||
      hasText(last) ||
      hasText(parentName) ||
      (parentEmail != null && String(parentEmail).trim() !== "" && !isPlaceholderEmail(parentEmail)) ||
      hasText(parentPhone) ||
      hasText(parentPreferredName) ||
      hasText(parentGender) ||
      hasText(parentZipCode) ||
      hasText(parentHighestEducationLevel) ||
      hasText(parentHearAboutUs);

    if (!showParentExtras) return list;

    if (showAllRegistrationFields || hasText(first) || hasText(last) || hasText(parentName)) {
      list.push({ label: "First Name", value: hasText(first) ? first : "—" });
      list.push({ label: "Last Name", value: hasText(last) ? last : "—" });
    }
    if (showAllRegistrationFields || hasText(parentPreferredName)) {
      list.push({
        label: "Preferred Name",
        value: hasText(parentPreferredName) ? String(parentPreferredName).trim() : "—",
      });
    }
    if (
      showAllRegistrationFields ||
      (parentEmail != null && String(parentEmail).trim() !== "" && !isPlaceholderEmail(parentEmail))
    ) {
      list.push({ label: "Email", value: displayEmailOrDash(parentEmail) });
    }
    if (showAllRegistrationFields || hasText(parentPhone)) {
      list.push({
        label: "Phone",
        value: hasText(parentPhone) ? formatPhoneDisplay(parentPhone) : "—",
      });
    }
    if (showAllRegistrationFields || hasText(parentGender)) {
      list.push({
        label: "Gender",
        value: getGenderDisplayLabel(parentGender),
      });
    }
    if (showAllRegistrationFields || hasText(parentZipCode)) {
      list.push({
        label: "Zip Code",
        value: hasText(parentZipCode) ? String(parentZipCode).trim() : "—",
      });
    }
    if (showAllRegistrationFields || hasText(parentHighestEducationLevel)) {
      list.push({
        label: "Highest Education Level Completed",
        value: getEducationLevelLabel(parentHighestEducationLevel),
        fullWidth: true,
      });
    }
    if (showAllRegistrationFields || hasText(parentHearAboutUs)) {
      list.push({
        label: "How Did You Hear About Us?",
        value: getHearAboutUsDisplayLabel(parentHearAboutUs, parentHearAboutUsOther),
        fullWidth: true,
      });
    }
    return list;
  }, [
    parentName,
    parentFirstName,
    parentLastName,
    parentEmail,
    parentPhone,
    parentPreferredName,
    parentGender,
    parentZipCode,
    parentHighestEducationLevel,
    parentHearAboutUs,
    parentHearAboutUsOther,
    showAllRegistrationFields,
  ]);

  const formClass = ACCOUNT_SIGNUP_FORM_CLASS;

  if (section === "profile") {
    return <div className={formClass}>{renderFormRows(profileFields)}</div>;
  }

  if (section === "parent") {
    if (parentFields.length === 0) {
      return (
        <p style={{ margin: 0, fontSize: "0.8125rem", color: "#6c757d", lineHeight: 1.45 }}>
          No parent or guardian information on file.
        </p>
      );
    }
    return <div className={formClass}>{renderFormRows(parentFields)}</div>;
  }

  return (
    <>
      {showPageChrome ? (
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              fontSize: "13px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#6b7280",
              marginBottom: "2px",
            }}
          >
            Account
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <h2
              id={titleId}
              style={{
                margin: 0,
                fontSize: "1.25rem",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Profile info
            </h2>
            {titleAction}
          </div>
        </div>
      ) : null}

      <div className={formClass}>{renderFormRows(profileFields)}</div>

      {parentFields.length > 0 ? (
        <div style={{ marginTop: "16px" }}>
          <div style={{ marginBottom: 8, fontSize: "0.875rem", fontWeight: 600, color: "#212529" }}>
            Parent / Guardian
          </div>
          <div className={formClass}>{renderFormRows(parentFields)}</div>
        </div>
      ) : null}
    </>
  );
}

export default ProfileInfoContent;
export { formatBirthMonthYearDisplay as formatBirthday, formatCreatedDate };
